import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Box, Flex, Icon, Spinner, styled, Text } from '@chilipiper/design-system'
import {
  Button,
  Events,
  Input,
  TextArea,
  Checkbox,
  Radio,
  InputNumber,
  Select,
  PillSelect,
} from '@chilipiper/design-system/src/new'
import {
  MeetingStartTimes,
  AvailabilityOptions,
  useSelectTimeSlot,
  SelectTimeSlotParams,
  useCalendar,
  mapSelectedDaySlots,
  GuestFormValues,
} from '@chilipiper/booking-flow'
import {
  FlexibleRoundRobinMeetingStartTime,
  MultipleRoundRobinMeetingStartTime,
} from '@chilipiper/api-client/src/user-availability'
import { IconError } from '@chilipiper/icons/figma-generated/src/icon'
import { DataFieldDataType } from '@chilipiper/api-client/src/chili-crm'
import { useTranslations } from '../../../context/TranslationContext'
import { Times, TimesEvents } from './Times'
import { Days } from './Days'
import { customerColorCss } from './styles'

type FieldType = {
  dataType?: DataFieldDataType
  description?: string
  error?: string
  id: string
  initialValue?: string
  hiddenValue?: string
  isRequired: boolean
  label?: string
}

interface Props {
  availableDays?: Record<string, boolean>
  fields?: Array<FieldType> | undefined
  guestEmail?: string
  isLoading: boolean
  meetingTypeId?: string
  onSchedule: (guestFormData: GuestFormValues) => void
  onSelectSlot: (params: SelectTimeSlotParams) => void
  selectedTime?: string
  timeSlots?: MeetingStartTimes
  availabilityOptions?: AvailabilityOptions
  timezone: string
  cancelRescheduling?: () => void
  daysWrapperRef: React.RefObject<HTMLDivElement | null>
  suggestedTimesToggle?: React.ReactNode
}

export const BookingMeeting = ({
  isLoading,
  meetingTypeId,
  onSelectSlot,
  timezone,
  onSchedule,
  selectedTime,
  fields,
  daysWrapperRef,
  guestEmail,
  suggestedTimesToggle,
  cancelRescheduling,
  timeSlots,
  availabilityOptions,
}: Props) => {
  let initialGuestForm = Object.fromEntries(
    fields
      ?.filter(field => field.initialValue || field.hiddenValue)
      .map(field => [field.id, field.hiddenValue ?? field.initialValue ?? '']) ?? []
  )
  if (guestEmail) {
    initialGuestForm = { ...initialGuestForm, PersonEmail: guestEmail }
  }
  const onSelectTimeSlot = useSelectTimeSlot(timeSlots, availabilityOptions, timezone)
  const [pickMeetingType, loadingDatesAndTimes, confirmMeeting] = useTranslations([
    'pickMeetingType',
    'loadingDatesAndTimes',
    'confirmMeeting',
  ])
  const [guestForm, setGuestForm] = useState<GuestFormValues>()
  const guestFormWithInitialData = { ...initialGuestForm, ...guestForm }
  const [immediateFieldErrors, setImmediateFieldErrors] = useState<Record<string, string>>()
  const visibleFields = fields
    ?.filter(field => !field.hiddenValue)
    .map(field => {
      return { ...field, error: immediateFieldErrors?.[field.id] ?? field.error }
    })
  const startTimesString = useMemo(() => {
    return (timeSlots?.startTimes ?? []).map(
      (
        startTime: MultipleRoundRobinMeetingStartTime | FlexibleRoundRobinMeetingStartTime | string
      ) => (typeof startTime === 'string' ? startTime : startTime.startTime)
    )
  }, [timeSlots])

  const { calendarDays, selectedDay, handleSelectDay } = useCalendar(timezone, startTimesString, 21)
  const hasVisibleFields = (visibleFields?.length ?? 0) > 0
  const emailInputRef = useRef<Events<'focus'>>(null)
  const buttonWrapperRef = useRef<HTMLDivElement>(null)
  const timesEventsRef = useRef<TimesEvents>(null)

  const startTimes = useMemo(() => {
    return mapSelectedDaySlots(selectedDay, timezone, startTimesString).map(
      slot => slot.defaultSlot
    )
  }, [selectedDay, timezone, startTimesString])

  const availableDays = useMemo(() => {
    return calendarDays.reduce(
      (acc, day) => {
        acc[day.date.toISOString()] = !day.isUnavailable
        return acc
      },
      {} as Record<string, boolean>
    )
  }, [calendarDays])

  useEffect(() => {
    let timeoutId: NodeJS.Timeout
    if (!daysWrapperRef.current) {
      return
    }
    const dayElement = daysWrapperRef.current.querySelector(
      `[data-day="${selectedDay.toDateString()}"]`
    )
    if (dayElement) {
      timeoutId = setTimeout(() => {
        dayElement.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' })
        timesEventsRef.current?.scrollIntoView()
      }, 10 /* For some reason there needs to be a delay here to work when changing timezones */)
    }
    return () => clearTimeout(timeoutId)
  }, [daysWrapperRef, selectedDay.toDateString()])

  if (!meetingTypeId) {
    return (
      <Flex
        flexDirection='column'
        justifyContent='center'
        alignItems='center'
        h='132px'
        data-test-id='BookingMeeting-noMeetingType'
      >
        <Icon color='fg/moderate' icon={IconError} mb={2} />
        <Text color='fg/moderate' textStyle='body-multiline' px={7} textAlign='center'>
          {pickMeetingType ?? 'Pick meeting type'}
        </Text>
      </Flex>
    )
  }
  if (isLoading) {
    return (
      <Flex justifyContent='center' alignItems='center' h='132px'>
        <Spinner mr={2} />
        <Text color='fg/moderate' textStyle='body-multiline'>
          {loadingDatesAndTimes ?? 'Loading dates and times'}
        </Text>
      </Flex>
    )
  }
  const email = (
    Array.isArray(guestFormWithInitialData['PersonEmail'])
      ? (guestFormWithInitialData['PersonEmail']?.[0] ?? '')
      : (guestFormWithInitialData['PersonEmail'] ?? '')
  ).trim()
  return (
    <form
      onInvalid={e => {
        if (
          'name' in e.target &&
          typeof e.target.name === 'string' &&
          'validationMessage' in e.target &&
          typeof e.target.validationMessage === 'string'
        ) {
          setImmediateFieldErrors({
            ...immediateFieldErrors,
            [e.target.name]: e.target.validationMessage,
          })
        }
      }}
      onSubmit={e => {
        e.preventDefault()
        onSchedule(guestFormWithInitialData)
      }}
    >
      <Days
        daysWrapperRef={daysWrapperRef}
        availableDays={availableDays}
        selectedDay={selectedDay}
        onSelectDay={handleSelectDay}
        timezone={timezone}
      />

      <Times
        eventsRef={timesEventsRef}
        time={selectedTime}
        cancelRescheduling={cancelRescheduling}
        onTimeChange={time => {
          if (!email) {
            emailInputRef.current?.focus()
          } else {
            buttonWrapperRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
          }
          onSelectSlot?.(onSelectTimeSlot(time))
          if (!hasVisibleFields) {
            onSchedule(guestFormWithInitialData)
          }
        }}
        timezone={timezone}
        shouldSubmitFormOnChange={!hasVisibleFields}
        startTimes={startTimes}
        suggestedTimesToggle={suggestedTimesToggle}
      />

      {hasVisibleFields && <Box boxShadow='border/row' h='1px' mb={5} />}
      {visibleFields?.map(field => {
        return (
          <Field
            key={field.id}
            field={field}
            value={
              Array.isArray(guestFormWithInitialData[field.id])
                ? (guestFormWithInitialData[field.id]?.[0] ?? '')
                : (guestFormWithInitialData[field.id] as string)
            }
            onChange={newValue => {
              if (field.error) {
                const newErrors = { ...immediateFieldErrors }
                delete newErrors[field.id]
                setImmediateFieldErrors(newErrors)
              }
              setGuestForm({ ...guestForm, [field.id]: newValue } as Omit<
                GuestFormValues,
                'location' | 'attendees'
              >)
            }}
          />
        )
      })}

      {hasVisibleFields && (
        <Box mx={5} mb={6} ref={buttonWrapperRef}>
          <ButtonThemeWrapper>
            <Button isFullWidth variant='major' isDisabled={!email || !selectedTime} type='submit'>
              {confirmMeeting ?? 'Confirm Meeting'}
            </Button>
          </ButtonThemeWrapper>
        </Box>
      )}
    </form>
  )
}

const ButtonThemeWrapper = styled(Box)`
  button:not([disabled]) {
    &,
    :hover,
    :active,
    :focus {
      ${customerColorCss}
    }
  }
`

const Field = ({
  field,
  value,
  onChange,
}: {
  field: FieldType
  onChange: (value: string) => void
  value: string
}) => {
  const commonProps = {
    mx: 5,
    mb: 3,
  }
  const label = field.label ?? ''
  const description = field.description ?? 'Enter...'
  if (
    field.dataType?.type === 'ShortText' ||
    field.dataType?.type === 'Email' ||
    field.dataType?.type === 'Phone'
  ) {
    return (
      <Input
        key={field.id}
        {...commonProps}
        variant='regular'
        type={(() => {
          if (field.dataType?.type === 'Email') {
            return 'email'
          }
          if (field.dataType?.type === 'Phone') {
            return 'tel'
          }
          return 'text'
        })()}
        name={field.id}
        label={<Input.Label position='top'>{label}</Input.Label>}
        placeholder={description}
        isRequired={field.isRequired}
        errorMessage={!value ? field.error : undefined}
        value={value}
        onChange={onChange}
      />
    )
  }
  if (field.dataType?.type === 'LongText') {
    return (
      <TextArea
        key={field.id}
        {...commonProps}
        variant='regular'
        label={<TextArea.Label position='top'>{label}</TextArea.Label>}
        placeholder={description}
        name={field.id}
        isRequired={field.isRequired}
        errorMessage={!value ? field.error : undefined}
        value={value}
        onChange={onChange}
      />
    )
  }
  if (field.dataType?.type === 'Number') {
    return (
      <InputNumber
        key={field.id}
        {...commonProps}
        variant='regular'
        label={<InputNumber.Label position='top'>{label}</InputNumber.Label>}
        placeholder={description}
        name={field.id}
        isRequired={field.isRequired}
        errorMessage={!value ? field.error : undefined}
        value={Number(value)}
        onChange={newValue => onChange(String(newValue))}
      />
    )
  }
  if (field.dataType?.type === 'Checkbox') {
    const selectedValues = value?.split(';').filter(value => value !== '') ?? []
    return (
      <Box key={field.id} {...commonProps}>
        <Text display='block' textStyle='form-label' color='fg/major' mb={1}>
          {field.label}
        </Text>
        <Text display='block' textStyle='body-multiline' color='fg/moderate' mb={3}>
          {field.description}
        </Text>
        <Box overflow='hidden' boxShadow='border/section' borderRadius={8}>
          <PillSelect
            variant='regular'
            isExpandable={true}
            label={
              <PillSelect.Label position='hidden'>
                {field.label ?? `Select ${field.dataType.type} value`}
              </PillSelect.Label>
            }
            onChange={value => {
              onChange(value.join(';'))
            }}
            values={selectedValues}
          >
            {field.dataType.values.map(value => (
              <PillSelect.Item key={value} value={value}>
                {value}
              </PillSelect.Item>
            ))}
          </PillSelect>
        </Box>
        {field.error && !value && (
          <Text
            display='block'
            textStyle='body-multiline-tight'
            data-test-id='input-error-message'
            color='fg/caution'
            mt={2}
          >
            {field.error}
          </Text>
        )}
      </Box>
    )
  }
  if (field.dataType?.type === 'RadioButton') {
    return (
      <Box key={field.id} {...commonProps}>
        <Radio.Group
          label={
            <Radio.GroupLabel position='top' description={field.description}>
              {label}
            </Radio.GroupLabel>
          }
          value={value}
          onChange={onChange}
          name={field.id}
        >
          {field.dataType.values.map(value => (
            <Radio.Item key={value} label={value} value={value} />
          ))}
        </Radio.Group>
        {field.error && !value && (
          <Text
            display='block'
            textStyle='body-multiline-tight'
            data-test-id='input-error-message'
            color='fg/caution'
            mt={2}
          >
            {field.error}
          </Text>
        )}
      </Box>
    )
  }
  if (field.dataType?.type === 'TrueFalse') {
    return (
      <Box key={field.id} {...commonProps}>
        <Box overflow='hidden' boxShadow='border/section' borderRadius={8}>
          <Checkbox
            label={<Checkbox.Label>{field.label ?? 'Checkbox'}</Checkbox.Label>}
            onChange={value => {
              onChange(value ? 'true' : 'false')
            }}
            isChecked={value === 'true'}
          />
        </Box>
      </Box>
    )
  }
  if (field.dataType?.type === 'Picklist') {
    return (
      <Select
        key={field.id}
        {...commonProps}
        label={
          <Select.Label position='top' description={field.description}>
            {label}
          </Select.Label>
        }
        errorMessage={!value ? field.error : undefined}
        value={value}
        onChange={onChange}
      >
        {field.dataType.values.map(value => (
          <Select.Item key={value} value={value}>
            {value}
          </Select.Item>
        ))}
      </Select>
    )
  }
  return null
}
